(() => {
  if (customElements.get('emergent-copy-link')) return;
  customElements.define('emergent-copy-link', class extends HTMLElement {
    connectedCallback() {
      this.controller?.abort();
      this.controller = new AbortController();
      const options = { signal: this.controller.signal };
      this.button = this.querySelector('[data-copy-link]');
      this.panel = this.querySelector('[data-copy-fallback]');
      this.input = this.querySelector('input');
      this.status = this.querySelector('[role="status"]');
      if (!this.button || !this.panel || !this.input || !this.status) return;
      this.input.value = this.productUrl();
      this.addEventListener('pointerenter', () => this.removeAttribute('data-tooltip-dismissed'), options);
      this.addEventListener('focusin', (event) => {
        if (event.target === this.button) this.removeAttribute('data-tooltip-dismissed');
      }, options);
      if (this.classList.contains('emergent-copy-link--corner')) {
        this.frame = this.closest('.emergent-media-share-frame');
        if (this.frame) {
          const schedule = () => {
            cancelAnimationFrame(this.positionFrame);
            this.positionFrame = requestAnimationFrame(() => this.positionInImage());
          };
          this.resizeObserver = new ResizeObserver(schedule);
          this.resizeObserver.observe(this.frame);
          this.frame.querySelectorAll('.product-media-container, img').forEach((media) => this.resizeObserver.observe(media));
          this.frame.addEventListener('scroll', schedule, { ...options, capture: true, passive: true });
          this.frame.addEventListener('slideChanged', schedule, { ...options, capture: true });
          schedule();
        }
      }
      this.button.addEventListener('click', () => this.copy(), options);
      this.querySelector('[data-copy-close]').addEventListener('click', () => this.close(true), options);
      this.input.addEventListener('click', () => this.input.select(), options);
      this.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') {
          if (!this.panel.hidden) this.close(true);
          this.setAttribute('data-tooltip-dismissed', '');
          event.stopPropagation();
        }
      }, options);
      document.addEventListener('keydown', (event) => {
        if (event.key === 'Escape' && this.matches(':hover')) this.setAttribute('data-tooltip-dismissed', '');
      }, options);
      document.addEventListener('pointerdown', (event) => {
        if (!this.contains(event.target)) this.close(false);
      }, options);
    }

    positionInImage() {
      const frame = this.frame.getBoundingClientRect();
      const viewer = this.frame.querySelector('.product__media-list');
      const clip = viewer?.getBoundingClientRect() || frame;
      let media = Array.from(this.frame.querySelectorAll('.product-media-container'));
      if (!media.length) media = Array.from(this.frame.querySelectorAll('img'));
      let target;
      let largest = 0;
      media.forEach((item) => {
        const rect = item.getBoundingClientRect();
        const width = Math.max(0, Math.min(rect.right, clip.right) - Math.max(rect.left, clip.left));
        const height = Math.max(0, Math.min(rect.bottom, clip.bottom) - Math.max(rect.top, clip.top));
        if (width * height > largest) {
          largest = width * height;
          target = rect;
        }
      });
      if (!target) return;
      this.style.setProperty('--copy-link-popup-max', `${Math.max(44, Math.min(target.right, clip.right) - 28)}px`);
      this.style.setProperty('--copy-link-bottom', `${frame.bottom - Math.min(target.bottom, clip.bottom) + 12}px`);
      this.style.setProperty('--copy-link-right', `${frame.right - Math.min(target.right, clip.right) + 12}px`);
    }

    productUrl() {
      const url = new URL(this.dataset.canonicalUrl);
      url.search = '';
      url.hash = '';
      const form = document.getElementById(this.dataset.productForm);
      const variant = form?.elements.namedItem('id')?.value || this.dataset.initialVariant;
      if (/^\d+$/.test(variant || '')) url.searchParams.set('variant', variant);
      return url.href;
    }

    async copy() {
      this.setAttribute('data-tooltip-dismissed', '');
      const url = this.productUrl();
      this.input.value = url;
      try {
        if (!navigator.clipboard?.writeText) throw new Error('Clipboard unavailable');
        await navigator.clipboard.writeText(url);
        if (!this.isConnected) return;
        this.close(false);
        this.status.textContent = this.dataset.copySuccess;
        this.status.hidden = false;
      } catch {
        if (!this.isConnected) return;
        this.status.textContent = this.dataset.copyFallback;
        this.status.hidden = false;
        this.panel.hidden = false;
        this.button.setAttribute('aria-expanded', 'true');
        this.input.focus();
        this.input.select();
      }
    }

    close(focus) {
      this.panel.hidden = true;
      this.status.hidden = true;
      this.button.setAttribute('aria-expanded', 'false');
      if (focus) this.button.focus();
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.resizeObserver?.disconnect();
      cancelAnimationFrame(this.positionFrame);
    }
  });
})();
